<template>
	<div class="native-list-offer" :class="{ 'native-list-offer--rendered': adStatus === 'rendered' }">
		<div
			:id="slotConfig.slotId"
			ref="adContainer"
			class="native-list-offer__container"
			v-observe-visibility="observeVisibilityOptions"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import {
	loadNativeSlot,
	destroyNativeSlot,
	type AdStatus,
	type GamResponseInfo,
} from '@/features/native-ads/helpers/gam-native-helper';
import { SnowplowFlexContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { NATIVE_AD_SLOTS } from '@/features/native-ads/helpers/native-ad-config';
import { MONETIZATION_TYPE_TRANSLATION_KEY, getTranslatableForShowOfferGraphql } from '@/helpers/offer-helper';
import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';
import { useNativeAdIframeTracking } from '@/features/native-ads/composables/useNativeAdIframeTracking';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { BuyboxRegularOffer } from '@/features/buybox/types/types';

const props = defineProps<{
	offer: BuyboxRegularOffer;
	title: TitleDetail;
	isPromotedBuyBox?: boolean;
	episodeNumber?: number | null;
	rowIndex?: number;
}>();

const { webLocale } = storeToRefs(useLanguageStore());
const { t } = useI18n();

const adStatus = ref<AdStatus>('loading');
const adContainer = ref<HTMLElement | null>(null);
const gamResponseInfo = ref<GamResponseInfo | null>(null);

const trackingContexts = computed(() =>
	gamResponseInfo.value ? [new SnowplowFlexContext({ data: gamResponseInfo.value })] : []
);

const slotConfig = computed(() => {
	if (props.episodeNumber != null) {
		const episodeConfig = NATIVE_AD_SLOTS['LIST_EPISODE_OFFER'];

		return { ...episodeConfig, slotId: `${episodeConfig.slotId}_${props.episodeNumber}` };
	}
	return props.isPromotedBuyBox ? NATIVE_AD_SLOTS.LIST_PROMOTED_BUYBOX_OFFER : NATIVE_AD_SLOTS.LIST_BUYBOX_OFFER;
});
const keyValueTargeting = 'native_list_offer' as const;
const trackingLabel = computed(() =>
	props.isPromotedBuyBox
		? NATIVE_AD_SLOTS.LIST_PROMOTED_BUYBOX_OFFER.slotId
		: NATIVE_AD_SLOTS.LIST_BUYBOX_OFFER.slotId
);

const clickthroughUrl = computed(() =>
	encodeURIComponent(
		'https://ad.doubleclick.net/ddm/trackclk/N1957659.279382DBMWARNERBROS.DI/B33320798.447199725;dc_trk_aid=640372217;dc_trk_cid=256085031;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1'
	)
);

const providerIcon = computed(() => formatImageUrl(props.offer.package.iconWide ?? '', 's160'));

const localPrice = computed(() => {
	const planRetailPrice = props.offer.plans?.[0]?.retailPrice ?? props.offer.package.planOffers?.[0]?.retailPrice;
	const retailPrice = props.offer.retailPrice ?? planRetailPrice ?? '';
	return t('WEBAPP_X_PER_MONTH', { price: retailPrice }).toString();
});

const ageRating = computed(() =>
	props.title.__typename !== 'Season' ? props.title.content.ageCertification ?? '' : ''
);

const descriptionString = computed(() => {
	const showText = getTranslatableForShowOfferGraphql(props.offer, props.title.objectType);
	const runtimeMins = props.title.content.runtime ? `${props.title.content.runtime}min` : '';
	return [showText, runtimeMins].filter(Boolean).join(' - ');
});

const { getIframeRenderStatus } = useNativeAdIframeTracking({
	adContainer,
	adStatus,
	onClickout: () => {
		TrackingHelper.trackEvent('ad', { action: 'click', label: trackingLabel.value }, trackingContexts.value);
	},
	onDestroyed: () => {
		adStatus.value = 'destroyed';
	},
});

const observeVisibilityOptions = computed(() => ({
	...mrcObservableOptions,
	callback: (isVisible: boolean) => {
		if (isVisible && adStatus.value === 'rendered') {
			TrackingHelper.trackEvent(
				'ad',
				{ action: 'impression', label: trackingLabel.value, nonInteraction: true },
				trackingContexts.value
			);
		}
	},
}));

onMounted(() => {
	loadNativeSlot({
		webLocale: webLocale.value,
		slotId: slotConfig.value.slotId,
		adUnitPath: slotConfig.value.adUnitPath,
		sizes: slotConfig.value.sizes,
		keyValueTargeting,
		targeting: {
			clickthrough_url: clickthroughUrl.value,
			provider_icon: providerIcon.value,
			monetization_type: t(MONETIZATION_TYPE_TRANSLATION_KEY[props.offer.monetizationType]),
			local_price: localPrice.value,
			age_rating: ageRating.value,
			// window.innerWidth is not available on the iframe, so we use GAM targeting to determing style breakpoints
			mobile_breakpoint: window.innerWidth < 1024 ? 'true' : 'false',
			// # is not a valid character for key value targeting. colour is built in gam from hex value, so we can just pass the hex without the #.
			background_color: (props.rowIndex ?? 0) % 2 === 0 ? '0c131e' : '111924',
			description_string: descriptionString.value,
			call_to_action: t('WEBAPP_WATCHNOW'),
		},
		onRendered: async () => {
			adStatus.value = await getIframeRenderStatus();
		},
		onFailed: () => {
			adStatus.value = 'failed';
		},
		onResponseInfo: info => {
			gamResponseInfo.value = info;
		},
	});
});

onBeforeUnmount(() => {
	destroyNativeSlot(slotConfig.value.slotId);
});

function formatImageUrl(url: string, profile: string): string {
	if (!url) return '';
	return `https://images.justwatch.com${url.replace('{profile}', profile).replace('{format}', 'jpg')}`;
}
</script>

<style lang="scss" scoped>
.native-list-offer {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 10;
	pointer-events: none;
	display: none;

	&--rendered {
		display: block;
		pointer-events: all;
	}

	&__container {
		width: 100%;
		height: 100%;
	}
}
</style>
